import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { Observable } from "rxjs";
import { BadgeComponent } from "@sxm-content-creators/tools-ds";
import { InteractiveFormComponent } from "../../components/interactive-form/interactive-form.component";
import { InteractiveFormSubmit, InteractiveFormTargetState } from "../../models/interactive-form.model";
import { toInteractiveFormValue, toUpdateFreeformRequest, toUpdatePollRequest } from "../../mappers/interactive.mapper";
import { SnackbarService } from "../../../components/snackbar/snackbar.service";
import { injectRouteInteractive } from "../inject-route-interactive";
import {
    INTERACTIVE_DISPLAY_STATE_BADGE_LIBRARY,
    InteractiveDisplayState,
    InteractiveDisplayStatePipe
} from "../../pipes/interactive-display-state.pipe";
import { FreeformApiService } from "../../services/freeform.api.service";
import { PollApiService } from "../../services/poll.api.service";
import { Interactive, getInteractiveDisplayState } from "../../models/interactive.model";

type EditableState = Extract<InteractiveDisplayState, "DRAFT" | "SCHEDULED" | "LIVE" | "PAUSED">;

// Determines what state an interactive can be updated into, depending on the current display state
const TARGET_STATES_BY_STATE: Record<EditableState, readonly InteractiveFormTargetState[]> = {
    DRAFT: ["DRAFT", "PUBLISHED"],
    SCHEDULED: ["DRAFT", "PUBLISHED"],
    LIVE: ["PUBLISHED"],
    PAUSED: ["PAUSED", "PUBLISHED"]
};
function isEditableState(state: InteractiveDisplayState): state is EditableState {
    return state in TARGET_STATES_BY_STATE;
}

@Component({
    selector: "app-edit-interactive",
    templateUrl: "./edit-interactive.component.html",
    styleUrl: "./edit-interactive.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [RouterLink, BadgeComponent, InteractiveFormComponent, InteractiveDisplayStatePipe]
})
export class EditInteractiveComponent {
    private readonly freeformApi = inject(FreeformApiService);
    private readonly pollApi = inject(PollApiService);
    private readonly router = inject(Router);
    private readonly snackbarService = inject(SnackbarService);

    readonly submitting = signal(false);

    readonly interactive = injectRouteInteractive();

    readonly formValue = computed(() => {
        const interactive = this.interactive();
        return interactive ? toInteractiveFormValue(interactive) : null;
    });

    readonly availableTargetStates = computed((): readonly InteractiveFormTargetState[] => {
        const interactive = this.interactive();
        if (!interactive) return [];
        const displayState = getInteractiveDisplayState(interactive);
        if (!isEditableState(displayState)) throw new Error(`${displayState} interactive is not editable`);
        return TARGET_STATES_BY_STATE[displayState];
    });

    onSubmit({ value, targetState }: InteractiveFormSubmit): void {
        const interactive = this.interactive();
        if (!interactive) return;

        const originalState = getInteractiveDisplayState(interactive);
        const request$: Observable<Interactive> =
            value.type === "poll"
                ? this.pollApi.update(interactive.id, toUpdatePollRequest(value, targetState))
                : this.freeformApi.update(interactive.id, toUpdateFreeformRequest(value, targetState));

        this.submitting.set(true);
        request$.subscribe({
            next: updated => {
                this.snackbarService.show(this.getSuccessMessage(originalState, updated), "success");
                void this.router.navigate(["/interactives"]);
            },
            error: () => {
                this.submitting.set(false);
                this.snackbarService.show("We couldn't save your changes. Try again.", "error");
            }
        });
    }

    private getSuccessMessage(before: InteractiveDisplayState, updated: Interactive): string {
        const after = getInteractiveDisplayState(updated);
        if (after === "SCHEDULED") return "Interactive scheduled.";
        if (before === "PAUSED" && after === "LIVE") return "Interactive unpaused. Interactive is now live.";
        if ((before === "DRAFT" || before === "SCHEDULED") && after === "LIVE") return "Interactive published.";
        return "Interactive saved.";
    }

    readonly badgeLibrary = INTERACTIVE_DISPLAY_STATE_BADGE_LIBRARY;
}
